import DownArrowIcon from "@/assets/svgs/down-arrow-icon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import { Tag } from "../ui/tag";
import { RadioInput } from "../ui/radio-input";
import { Textarea } from "../ui/textarea";
import { Controller, useForm } from "react-hook-form";
import { api } from "../utils/convex-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { Tab } from "../ui/tab";
import { useSheet } from "../shared/sheet-provider";
import { SheetIds } from "../utils/sheet-ids";
import TutorSubjectYearGroups from "./tutor-subject-year-groups";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { Button } from "../ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReferenceInfoSheetPayload,
  ReferenceInfoSheetResult,
} from "../sheets/reference-info-sheet";
import { buildCurriculumYearGroupTabs } from "../sheets/multi-select-sheet";
import {
  isBritishCurriculumLabel,
  isIbCurriculumLabel,
} from "../utils/curriculum";

function sameIdSet(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function clearSubjectYearGroups(
  subjectYearGroups: { subjectId: string; yearGroupIds: string[] }[],
) {
  return subjectYearGroups.map((subject) => ({
    ...subject,
    yearGroupIds: [],
  }));
}

const TutorBioFormSchema = z.object({
  countryOfResidenceId: z.string().min(1),
  languageIds: z.array(z.string()).min(1),
  curriculumIds: z.array(z.string()).min(1),
  subjectYearGroups: z
    .array(
      z.object({
        subjectId: z.string(),
        yearGroupIds: z.array(z.string()).min(1),
      })
    )
    .min(1),
  detailsOfEducationalDegree: z.optional(z.string()),
  teachingExperience: z.optional(z.string()),
  examBoardMarker: z.boolean(),
  detailsOfExamBoard: z.optional(z.string()),
  currentWorkplace: z.optional(z.string()),
  bio: z.optional(z.string()),
  additionalInfo: z.optional(z.string()),
});
type TutorBioFormSchemaType = z.infer<typeof TutorBioFormSchema>;

function TutorBioForm({
  initialData,
  onScrollToTop,
}: {
  initialData: typeof api.tutor.profileSettings.queries.getProfile._returnType;
  onScrollToTop: () => void;
}) {
  const { open: openMultiSelect } = useSheet<any, any>(SheetIds.MULTI_SELECT);
  const { open: openSingleSelect } = useSheet<any, any>(SheetIds.SINGLE_SELECT);
  const { open: openReferenceInfo } = useSheet<
    ReferenceInfoSheetPayload,
    ReferenceInfoSheetResult
  >(SheetIds.REFERENCE_INFO);

  const selectedSubjects = (initialData.subjects ?? []).map((subject) => ({
    subjectId: subject.subjectId,
    yearGroupIds: subject.yearGroups.map(
      (yearGroup: { yearGroupId: string }) => yearGroup.yearGroupId
    ),
  })) as {
    subjectId: string;
    yearGroupIds: string[];
  }[];

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<TutorBioFormSchemaType>({
    resolver: zodResolver(TutorBioFormSchema),
    defaultValues: {
      countryOfResidenceId: initialData.countryOfResidenceId,
      languageIds: initialData.languageIds,
      curriculumIds: initialData.curriculumIds,
      subjectYearGroups: selectedSubjects,
      detailsOfEducationalDegree: initialData.detailsOfEducationalDegree,
      teachingExperience: initialData.teachingExperience,
      examBoardMarker: initialData.examBoardMarker ?? false,
      detailsOfExamBoard: initialData.detailsOfExamBoard,
      currentWorkplace: initialData.currentWorkplace,
      bio: initialData.bio,
      additionalInfo: initialData.additionalInfo,
    },
    mode: "onChange",
  });
  const { data: availableCountries = [] } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getAvailableCountries, {})
  );
  const { data: allSubjects = [] } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getAllSubjects, {})
  );
  const { data: allCurriculum = [] } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getAvailableCurriculums, {})
  );
  const { data: allLanguages = [] } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getAvailableLanguages, {})
  );
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    null
  );

  const curriculumIds = watch("curriculumIds");
  const subjectYearGroups = watch("subjectYearGroups");

  const ibCurriculumId = useMemo(
    () =>
      allCurriculum.find((curriculum) =>
        isIbCurriculumLabel(curriculum.label),
      )?.value,
    [allCurriculum],
  );

  const hasIbCurriculum = useMemo(
    () =>
      !!ibCurriculumId &&
      curriculumIds.some((curriculumId) => curriculumId === ibCurriculumId),
    [curriculumIds, ibCurriculumId],
  );

  const hasBritishCurriculum = useMemo(
    () =>
      curriculumIds.some((curriculumId) =>
        isBritishCurriculumLabel(
          allCurriculum.find((curriculum) => curriculum.value === curriculumId)
            ?.label,
        ),
      ),
    [curriculumIds, allCurriculum],
  );

  const yearGroupLabel = hasIbCurriculum && !hasBritishCurriculum
    ? "Grades"
    : "Year groups";
  const selectYearGroupTitle =
    hasIbCurriculum && !hasBritishCurriculum
      ? "Select grades"
      : "Select year groups";

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.tutor.profileSettings.queries.getProgrammesForCurriculum,
      hasIbCurriculum && ibCurriculumId
        ? { curriculumId: ibCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  const programmeIdsForYearGroups = useMemo(
    () => programmes.map((programme) => programme.value),
    [programmes],
  );

  const { data: allYearGroups = [] } = useQuery(
    convexQuery(
      api.tutor.profileSettings.queries.getYearGroupsListBySubjectId,
      selectedSubjectId
        ? {
            subjectId: selectedSubjectId as Id<"subjects">,
            curriculumIds: curriculumIds as Id<"curriculum">[],
            programmeIds:
              hasIbCurriculum && programmeIdsForYearGroups.length > 0
                ? (programmeIdsForYearGroups as Id<"keyStages">[])
                : undefined,
          }
        : "skip",
    ),
  );

  const [firstSubjectYearGroups] = selectedSubjects ?? [];
  const firstSubjectId = firstSubjectYearGroups?.subjectId;
  const examBoardMarker = watch("examBoardMarker");
  const isSaveEnabled = isValid;

  useEffect(() => {
    if (firstSubjectId) {
      setSelectedSubjectId(firstSubjectId);
    }
  }, [firstSubjectId]);

  const handleCountryOfResidenceOpen = useCallback(
    async (preSelectedOption: string, onChange: (value: string) => void) => {
      const result = await openSingleSelect({
        options: availableCountries,
        preSelectedOption: preSelectedOption,
      });
      if (result?.selectedOption) {
        onChange(result.selectedOption);
      }
    },
    [openSingleSelect, availableCountries]
  );

  const handleLanguageOptionsOpen = useCallback(
    async (
      preSelectedOptions: string[],
      onChange: (value: string[]) => void
    ) => {
      const result = await openMultiSelect({
        options: allLanguages,
        preSelectedOptions: preSelectedOptions,
      });
      if (result?.selectedOptions) {
        onChange(result.selectedOptions);
      }
    },
    [openMultiSelect, allLanguages]
  );

  const applyCurriculumSelection = useCallback(
    (nextCurriculumIds: string[], onChange: (value: string[]) => void) => {
      if (sameIdSet(nextCurriculumIds, curriculumIds)) return;

      onChange(nextCurriculumIds);
      setValue(
        "subjectYearGroups",
        clearSubjectYearGroups(subjectYearGroups),
        { shouldValidate: true },
      );
    },
    [curriculumIds, setValue, subjectYearGroups],
  );

  const handleCurriculumOptionsOpen = useCallback(
    async (
      preSelectedOptions: string[],
      onChange: (value: string[]) => void
    ) => {
      const result = await openMultiSelect({
        options: allCurriculum,
        preSelectedOptions: preSelectedOptions,
        title: "Which curriculum do you teach?",
      });
      if (!result?.selectedOptions) return;
      applyCurriculumSelection(result.selectedOptions, onChange);
    },
    [openMultiSelect, allCurriculum, applyCurriculumSelection]
  );

  const handleYearGroupOptionsOpen = useCallback(
    async (
      preSelectedOptions: string[],
      onChange: (value: string[]) => void
    ) => {
      const tabs = buildCurriculumYearGroupTabs({
        yearGroups: allYearGroups,
        curriculums: allCurriculum,
        selectedCurriculumIds: curriculumIds,
      });
      const result = await openMultiSelect({
        options: allYearGroups,
        preSelectedOptions: preSelectedOptions,
        title: selectYearGroupTitle,
        tabs,
      });
      if (
        result?.selectedOptions &&
        !sameIdSet(result.selectedOptions, preSelectedOptions)
      ) {
        onChange(result.selectedOptions);
      }
    },
    [
      openMultiSelect,
      allYearGroups,
      allCurriculum,
      curriculumIds,
      selectYearGroupTitle,
    ]
  );

  const handleSubjectsOptionsOpen = useCallback(
    async (
      preSelectedOptions: string[],
      value: { subjectId: string; yearGroupIds: string[] }[],
      onChange: (value: { subjectId: string; yearGroupIds: string[] }[]) => void
    ) => {
      const result = await openMultiSelect({
        options: allSubjects,
        preSelectedOptions: preSelectedOptions,
      });
      if (result?.selectedOptions) {
        onChange(
          result.selectedOptions.map((subjectId: string) => ({
            subjectId: subjectId,
            yearGroupIds:
              value.find((subjectData) => subjectData.subjectId === subjectId)
                ?.yearGroupIds ?? [],
          }))
        );
      }
    },
    [openMultiSelect, allSubjects]
  );

  const { mutateAsync: updateProfile, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.updateProfile
    ),
  });

  const handleSave = handleSubmit(async (data) => {
    try {
      await updateProfile({
        countryOfResidenceId:
          data.countryOfResidenceId as Id<"countriesOfResidences">,
        languageIds: data.languageIds as Id<"languages">[],
        curriculumIds: data.curriculumIds as Id<"curriculum">[],
        detailsOfEducationalDegree: data.detailsOfEducationalDegree,
        teachingExperience: data.teachingExperience,
        examBoardMarker: data.examBoardMarker,
        detailsOfExamBoard: data.detailsOfExamBoard,
        currentWorkplace: data.currentWorkplace,
        bio: data.bio,
        subjectYearGroups: data.subjectYearGroups.map((subject) => ({
          subjectId: subject.subjectId as Id<"subjects">,
          yearGroupIds: subject.yearGroupIds as Id<"yearGroups">[],
        })),
        additionalInfo: data.additionalInfo,
      });
      Toast.show({ type: "success", text1: "Saved" });
      onScrollToTop();
    } catch {
      Toast.show({ type: "error", text1: "Failed to save" });
    }
  });

  return (
    <View className="gap-6">
      <Controller
        control={control}
        name="countryOfResidenceId"
        render={({ field: { value, onChange } }) => {
          const countryOfResidence = availableCountries.find(
            (country) => country.value === value
          );
          return (
            <View className="gap-2">
              <Pressable
                className="flex-row items-center gap-2"
                onPress={() => handleCountryOfResidenceOpen(value, onChange)}
              >
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Country of residence
                </Text>
                <DownArrowIcon pathClassName="stroke-dark" />
              </Pressable>
              <View className="gap-4 flex-row">
                <Tag size="md" className="self-start w-auto">
                  <Tag.Text size="md">{countryOfResidence?.label}</Tag.Text>
                </Tag>
              </View>
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="languageIds"
        render={({ field: { value, onChange } }) => {
          const languages =
            allLanguages.filter((language) => value.includes(language.value)) ??
            [];

          return (
            <View className="gap-2">
              <Pressable
                className="flex-row items-center gap-2"
                onPress={() => handleLanguageOptionsOpen(value, onChange)}
              >
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Spoken languages
                </Text>
                <DownArrowIcon pathClassName="stroke-dark" />
              </Pressable>
              <ScrollView
                horizontal
                contentContainerClassName="gap-4"
                showsHorizontalScrollIndicator={false}
              >
                {languages.map((language) => (
                  <Tag
                    key={language.value}
                    size="md"
                    className="self-start w-auto"
                    close
                  >
                    <Tag.Text size="md">{language.label}</Tag.Text>
                    <Tag.Close
                      onPress={() =>
                        onChange(
                          value.filter(
                            (languageId) => languageId !== language.value
                          )
                        )
                      }
                    />
                  </Tag>
                ))}
              </ScrollView>
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="curriculumIds"
        render={({ field: { value, onChange } }) => {
          const curriculums =
            allCurriculum.filter((curriculum) =>
              value.includes(curriculum.value)
            ) ?? [];

          return (
            <View className="gap-2">
              <Pressable
                className="flex-row items-center gap-2"
                onPress={() => handleCurriculumOptionsOpen(value, onChange)}
              >
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Curricula taught
                </Text>
                <DownArrowIcon pathClassName="stroke-dark" />
              </Pressable>
              <ScrollView
                horizontal
                contentContainerClassName="gap-4"
                showsHorizontalScrollIndicator={false}
              >
                {curriculums.map((curriculum) => (
                  <Tag
                    key={curriculum.value}
                    size="md"
                    className="self-start w-auto"
                    close
                  >
                    <Tag.Text size="md">{curriculum.label}</Tag.Text>
                    <Tag.Close
                      onPress={() =>
                        applyCurriculumSelection(
                          value.filter(
                            (curriculumId) =>
                              curriculumId !== curriculum.value
                          ),
                          onChange
                        )
                      }
                    />
                  </Tag>
                ))}
              </ScrollView>
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="subjectYearGroups"
        render={({ field: { value, onChange } }) => {
          const selectedSubjects = allSubjects.filter((subject) =>
            value.some((subjectData) => subjectData.subjectId === subject.value)
          );
          const selectedSubjectYearGroups =
            value.find(
              (subjectData) => subjectData.subjectId === selectedSubjectId
            )?.yearGroupIds ?? [];

          const onYearGroupSelect = (yearGroupIds: string[]) => {
            onChange(
              value.map((subjectData) =>
                subjectData.subjectId === selectedSubjectId
                  ? { ...subjectData, yearGroupIds }
                  : subjectData
              )
            );
          };

          return (
            <View className="gap-4">
              <View className="gap-2">
                <Pressable
                  className="flex-row items-center gap-2"
                  onPress={() =>
                    handleSubjectsOptionsOpen(
                      selectedSubjects.map((subject) => subject.value),
                      value,
                      onChange
                    )
                  }
                >
                  <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                    Subjects taught
                  </Text>
                  <DownArrowIcon pathClassName="stroke-dark" />
                </Pressable>
                <ScrollView
                  horizontal
                  contentContainerClassName="gap-4"
                  showsHorizontalScrollIndicator={false}
                >
                  {selectedSubjects.map((subject) => (
                    <Tag
                      key={subject.value}
                      size="md"
                      className="self-start w-auto"
                      close
                    >
                      <Tag.Text size="md">{subject.label}</Tag.Text>
                      <Tag.Close
                        onPress={() => {
                          const newSelectedSubjects = value.filter(
                            (subjectData) =>
                              subjectData.subjectId !== subject.value
                          );
                          onChange(newSelectedSubjects);
                          const [newSelectedFirstSubject] = newSelectedSubjects;
                          setSelectedSubjectId(
                            newSelectedFirstSubject?.subjectId ?? null
                          );
                        }}
                      />
                    </Tag>
                  ))}
                </ScrollView>
              </View>
              <View className="h-[1px] w-full bg-gray-200" />
              <View className="gap-2">
                <ScrollView
                  horizontal
                  contentContainerClassName="gap-4"
                  showsHorizontalScrollIndicator={false}
                >
                  {selectedSubjects.map((subject) => (
                    <Tab
                      key={subject.value}
                      intent={
                        selectedSubjectId === subject.value
                          ? "active"
                          : "inActive"
                      }
                      onPress={() => {
                        setSelectedSubjectId(subject.value);
                      }}
                      size="md"
                    >
                      <Tab.Text size="md">{subject.label}</Tab.Text>
                    </Tab>
                  ))}
                </ScrollView>
                <View className="gap-2">
                  <Pressable
                    className="flex-row items-center gap-2"
                    onPress={() =>
                      handleYearGroupOptionsOpen(
                        selectedSubjectYearGroups,
                        onYearGroupSelect
                      )
                    }
                  >
                    <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                      {yearGroupLabel}
                    </Text>
                    <DownArrowIcon pathClassName="stroke-dark" />
                  </Pressable>
                  {!!selectedSubjectId && (
                    <TutorSubjectYearGroups
                      yearGroups={allYearGroups}
                      yearGroupIds={selectedSubjectYearGroups}
                      onYearGroupSelect={onYearGroupSelect}
                    />
                  )}
                </View>
              </View>
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="detailsOfEducationalDegree"
        render={({ field: { value, onChange } }) => {
          return (
            <View className="gap-2">
              <View className="flex-row items-center gap-2">
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Details of your educational degree
                </Text>
              </View>
              <Textarea
                placeholder="eg: BSc in Biology, BA in English Literature"
                size="sm"
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                value={value}
                onChangeText={onChange}
              />
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="teachingExperience"
        render={({ field: { value, onChange } }) => {
          return (
            <View className="gap-2">
              <View className="flex-row items-center gap-2">
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Years of teaching experience
                </Text>
              </View>
              <Textarea
                placeholder="eg: 5+ teaching, 4+ tutoring"
                size="sm"
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                value={value}
                onChangeText={onChange}
              />
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="examBoardMarker"
        render={({ field: { value, onChange } }) => {
          return (
            <View className="gap-2">
              <View className="flex-row items-center gap-2">
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Are you an exam board marker?
                </Text>
              </View>
              <View className="flex-row gap-2">
                <RadioInput onPress={() => onChange(true)}>
                  <RadioInput.Label>Yes</RadioInput.Label>
                  <RadioInput.Icon checked={value} />
                </RadioInput>
                <RadioInput
                  onPress={() => {
                    onChange(false);
                    setValue("detailsOfExamBoard", "", {
                      shouldValidate: true,
                    });
                  }}
                >
                  <RadioInput.Label>No</RadioInput.Label>
                  <RadioInput.Icon checked={!value} />
                </RadioInput>
              </View>
            </View>
          );
        }}
      />

      {examBoardMarker && (
        <Controller
          control={control}
          name="detailsOfExamBoard"
          render={({ field: { value, onChange } }) => {
            return (
              <View className="gap-2">
                <View className="flex-row items-center gap-2">
                  <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                    Details of the exam board
                  </Text>
                </View>
                <Textarea
                  placeholder="eg: Board name, subject, curriculum"
                  size="sm"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  value={value}
                  onChangeText={onChange}
                />
              </View>
            );
          }}
        />
      )}

      <Controller
        control={control}
        name="currentWorkplace"
        render={({ field: { value, onChange } }) => {
          return (
            <View className="gap-2">
              <View className="flex-row items-center gap-2">
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  What is your latest teaching position?
                </Text>
              </View>
              <Textarea
                placeholder="eg: Year 6 Teacher, Secondary Science Tutor, Head of English, previous head of Maths, SENCO coordinator"
                size="sm"
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                value={value}
                onChangeText={onChange}
              />
            </View>
          );
        }}
      />

      <Controller
        control={control}
        name="bio"
        render={({ field: { value, onChange } }) => {
          return (
            <View className="gap-2">
              <View className="flex-row items-center gap-2 justify-between">
                <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
                  Your bio
                </Text>
                <Controller
                  control={control}
                  name="additionalInfo"
                  render={({ field: { value, onChange } }) => (
                    <Text
                      className="text-base text-teal-600 leading-[1.5] font-medium font-geologica-medium"
                      onPress={async () => {
                        const result = await openReferenceInfo({
                          initialReferenceInfo: value,
                        });
                        if (result?.referenceInfo) {
                          onChange(result.referenceInfo);
                        }
                      }}
                    >
                      Reference info provided by you
                    </Text>
                  )}
                />
              </View>
              <Textarea
                placeholder="This is the first thing parents will see on your profile. Write a short introduction introducing yourself to our families, highlight your experience, areas of expertise and approach 
(max 500 characters)"
                size="sm"
                multiline
                numberOfLines={6}
                textAlignVertical="top"
                value={value}
                onChangeText={onChange}
                className="h-[176px]"
              />
            </View>
          );
        }}
      />

      <View className="items-end mt-3 mb-4">
        <Button
          size="sm"
          intent="primary"
          className="px-12"
          onPress={handleSave}
          disabled={!isSaveEnabled || isPending}
        >
          <Button.Text>Save</Button.Text>
        </Button>
      </View>
    </View>
  );
}

export default TutorBioForm;
